import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Mail, ArrowLeft, ArrowRight, Wand2, Loader2, Check,
  Eye, EyeOff, Users, Star, Shield, User, Lock,
  BookOpen, Zap, Brain, Mic, GraduationCap, Sparkles
} from 'lucide-react';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const LoginPage = () => {
  const [step, setStep] = useState('welcome');
  const [loginMethod, setLoginMethod] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [emailValid, setEmailValid] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [returningUser, setReturningUser] = useState(null);
  
  const { login, register, verify2faLogin } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  
  const [twoFaUserId, setTwoFaUserId] = useState(null);
  const [twoFaEmailHint, setTwoFaEmailHint] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');
  
  const authError = location.state?.error;

  useEffect(() => {
    const savedEmail = localStorage.getItem('istudypro_email');
    const savedName = localStorage.getItem('istudypro_name');
    if (savedEmail) {
      setReturningUser({ email: savedEmail, name: savedName });
      setEmail(savedEmail);
    }
    // Honor ?mode=signup so the "Get Started" CTA on the marketing nav
    // lands users in registration mode (skipping the welcome step) without
    // needing an extra click.
    if (searchParams.get('mode') === 'signup') {
      setIsNewUser(true);
      setStep('email-input');
    }
  }, [searchParams]);

  useEffect(() => {
    if (step === 'email-input' && emailInputRef.current) {
      setTimeout(() => emailInputRef.current?.focus(), 300);
    }
    if (step === 'password-input' && passwordInputRef.current) {
      setTimeout(() => passwordInputRef.current?.focus(), 300);
    }
  }, [step]);

  useEffect(() => {
    if (email.length === 0) setEmailValid(null);
    else setEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  }, [email]);

  useEffect(() => {
    if (!password) { setPasswordStrength(0); return; }
    let s = 0;
    if (password.length >= 6) s += 25;
    if (password.length >= 8) s += 25;
    if (/[A-Z]/.test(password)) s += 25;
    if (/[0-9!@#$%^&*]/.test(password)) s += 25;
    setPasswordStrength(s);
  }, [password]);

  const triggerConfetti = () => {
    confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, colors: ['#1ED760', '#FF715B', '#F5A623', '#3B82F6'] });
  };

  const handleGoogleLogin = async () => {
    setLoginMethod('google');
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/auth/google/redirect`);
      const data = await res.json();
      if (!data?.authorization_url) throw new Error('No authorization URL');
      window.location.href = data.authorization_url;
    } catch (err) {
      setLoading(false);
      setLoginMethod(null);
    }
  };

  const handleEmailNext = async (e) => {
    e?.preventDefault();
    if (!emailValid) return;
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/auth/check-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (response.ok) {
        try {
          const data = await response.json();
          setIsNewUser(!data.exists);
          if (data.exists && data.name) setReturningUser({ email, name: data.name });
        } catch {}
      }
    } catch {  }
    setLoading(false);
    setStep('password-input');
  };

  const handleMagicLink = async () => {
    if (!emailValid) return;
    setLoginMethod('magic');
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/auth/magic-link/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      let data = {};
      try { data = await response.json(); } catch {}
      if (!response.ok) throw new Error(data.detail || 'Failed to send magic link');
      setStep('magic-sent');
      if (rememberMe) localStorage.setItem('istudypro_email', email);
      if (data.magic_link) {
        toast.success(
          <div><p>Magic link generated!</p><a href={data.magic_link} className="text-[var(--isp-accent)] underline text-sm" target="_blank" rel="noopener noreferrer">Click here to sign in (dev mode)</a></div>,
          { duration: 10000 }
        );
      }
    } catch (error) { toast.error(error.message || 'Failed to send magic link'); }
    finally { setLoading(false); }
  };

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    if (!password || (isNewUser && !name)) return;
    setLoginMethod('password');
    setLoading(true);
    try {
      if (isNewUser) {
        await register(email, password, name);
        if (rememberMe) { localStorage.setItem('istudypro_email', email); localStorage.setItem('istudypro_name', name); }
      } else {
        const result = await login(email, password);
        if (result?.requires_2fa) {
          setTwoFaUserId(result.user_id);
          setTwoFaEmailHint(result.email_hint || '');
          if (result.fallback_code) {
            setTwoFaCode(result.fallback_code);
            toast.info('Email unavailable — code auto-filled.');
          }
          setStep('2fa-verify');
          setLoading(false);
          return;
        }
        if (rememberMe) localStorage.setItem('istudypro_email', email);
      }
      setStep('success');
      triggerConfetti();
      // Explicit navigation — we no longer rely solely on the route-guard
      // re-render (`user ? <Navigate to="/app" /> : <LoginPage />`) because
      // framer-motion's AnimatePresence with mode="wait" can stall the
      // re-render of <Navigate /> long enough for the user to perceive a
      // "stuck on success screen" beat. 1200ms lets the success animation
      // play once and then hard-nav to /app.
      setTimeout(() => {
        setLoading(false);
        navigate('/app', { replace: true });
      }, 1200);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Incorrect email or password. Please try again.');
      setLoading(false);
    }
  };

  const getStepProgress = () => {
    const map = { 'welcome': 0, 'email-input': 33, 'password-input': 66, '2fa-verify': 85, 'magic-sent': 100, 'success': 100 };
    return map[step] || 0;
  };

  const handle2faVerify = async (e) => {
    e?.preventDefault();
    if (twoFaCode.length !== 6) return;
    setLoading(true);
    try {
      await verify2faLogin(twoFaUserId, twoFaCode);
      setStep('success');
      triggerConfetti();
      setTimeout(() => {
        setLoading(false);
        navigate('/app', { replace: true });
      }, 1200);
    }
    catch (error) { toast.error(error.response?.data?.detail || 'Invalid code. Try again.'); setLoading(false); }
  };

  const features = [
    { icon: Mic, label: 'Live Transcription', color: 'text-[var(--isp-accent)]' },
    { icon: Brain, label: 'AI Study Buddy', color: 'text-[var(--isp-coral)]' },
    { icon: GraduationCap, label: 'Exam Prep', color: 'text-[var(--isp-amber)]' },
    { icon: Zap, label: 'Smart Flashcards', color: 'text-[var(--isp-blue)]' },
  ];

  return (
    <div
      className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 py-10"
      data-testid="login-page"
      style={{
        // Soft Klein-blue → near-white gradient. Light by default; the
        // glass card adapts automatically via the page's existing tokens.
        background:
          'radial-gradient(1200px 600px at 20% -10%, rgba(0, 47, 167, 0.18) 0%, rgba(0, 47, 167, 0) 60%),' +
          'radial-gradient(900px 500px at 110% 110%, rgba(220, 234, 255, 0.55) 0%, rgba(220, 234, 255, 0) 60%),' +
          'linear-gradient(180deg, #F5F7FB 0%, #E8EEFB 100%)',
        // Light-mode token overrides — the inner auth steps (and shadcn
        // Input components) reference these variables, so mapping them
        // to slate/wave colors here gives us a coherent light surface
        // without rewriting every nested element.
        '--isp-page': '#F5F7FB',
        '--isp-glass': 'rgba(255, 255, 255, 0.85)',
        '--isp-glass-inner': '#FFFFFF',
        '--isp-glass-border': 'rgba(15, 23, 42, 0.08)',
        '--isp-heading': '#0F172A',
        '--isp-body': '#475569',
        '--isp-muted': '#64748B',
        '--isp-accent': '#002FA7',
        '--isp-coral': '#B8480E',
        '--isp-amber': '#B45309',
        color: '#0F172A',
      }}
    >
      {/* Decorative blobs — gentle motion, low opacity so they read as
          ambient warmth without competing with the auth card. */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <motion.div
          className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full opacity-50"
          style={{ background: 'radial-gradient(circle, rgba(0,47,167,0.22) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.1, 1], x: [0, 28, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-32 -right-32 w-[460px] h-[460px] rounded-full opacity-60"
          style={{ background: 'radial-gradient(circle, rgba(232, 224, 251, 0.7) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.08, 1], y: [0, -22, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/3 right-1/3 w-[280px] h-[280px] rounded-full opacity-40"
          style={{ background: 'radial-gradient(circle, rgba(252, 228, 210, 0.6) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.12, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Top-left back-to-home — visible on all breakpoints */}
      <Link
        to="/"
        className="absolute top-5 left-5 z-20 inline-flex items-center gap-2 text-slate-600 hover:text-[#002FA7] transition-colors group"
        data-testid="back-to-landing"
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" aria-hidden />
        <span className="text-sm font-semibold">Home</span>
      </Link>

      {/* Logo top-right — anchors the brand without dominating */}
      <Link to="/" className="absolute top-5 right-6 z-20 flex items-center gap-2" data-testid="login-logo">
        <img src="/logo-main.png" alt="iStudyPro" className="w-8 h-8 object-contain" />
        <span className="font-black text-base tracking-tight">
          <span className="text-slate-900">iStudy</span>
          <span className="text-[#002FA7]">Pro</span>
        </span>
      </Link>

      {/* Auth card — glassmorphic, centered, single column. Replaces the
          50/50 split. Title typography uses font-serif-accent (Raleway)
          to match the Wave-redesigned dashboard greeting. */}
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
        className="w-full max-w-md relative z-10"
      >
        {/* Subtle headline above the card — adds context without taking
            up card real estate. */}
        <div className="text-center mb-6">
          <h1 className="font-serif-accent text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
            Record your lecture.
            <br />
            <span className="text-[#002FA7]">We do the rest.</span>
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-sm mx-auto">
            The daily 10-minute habit that turns four years of lectures into a degree.
          </p>
        </div>

        {/* Progress bar — slim, sits on top of the glass card */}
        <div className="h-1 w-full bg-white/60 rounded-full overflow-hidden mb-3">
          <motion.div
            className="h-full bg-gradient-to-r from-[#002FA7] to-[#4F2BB8]"
            initial={{ width: '0%' }}
            animate={{ width: `${getStepProgress()}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>

        <div
          className="relative bg-white/70 backdrop-blur-xl border border-white/60 rounded-3xl shadow-[0_20px_60px_-20px_rgba(0,47,167,0.25)] p-7 sm:p-9"
          data-testid="login-card"
        >
          <AnimatePresence mode="wait">
              {/* ── WELCOME STEP ── */}
              {step === 'welcome' && (
                <motion.div key="welcome" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                  <div className="text-center mb-8">
                    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1, type: "spring" }}>
                      <div className="w-16 h-16 mx-auto mb-5  overflow-hidden flex items-center justify-center lg:hidden">
                        <img src="/logo-main.png" alt="iStudyPro" className="w-full h-full object-contain" />
                      </div>
                    </motion.div>
                    {returningUser ? (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                          Welcome back{returningUser.name ? `, ${returningUser.name.split(' ')[0]}` : ''}!
                        </h1>
                        <p className="text-[var(--isp-body)] text-sm">Ready to continue your learning journey?</p>
                      </motion.div>
                    ) : (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                          Welcome to <span className="text-[var(--isp-accent)]">iStudyPro</span>
                        </h1>
                        <p className="text-[var(--isp-body)] text-sm">Your AI-powered study companion</p>
                      </motion.div>
                    )}
                  </div>

                  {authError && (
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                      className="mb-6 p-3 rounded-xl bg-[var(--isp-coral)]/10 border border-[var(--isp-coral)]/20 text-[var(--isp-coral)] text-sm text-center"
                    >{authError}</motion.div>
                  )}

                  {/* Google Login */}
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-3">
                    <Button
                      onClick={handleGoogleLogin}
                      disabled={loading && loginMethod === 'google'}
                      className="w-full h-14  bg-white hover:bg-white/95 text-gray-900 font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 group"
                      data-testid="google-login-button"
                    >
                      {loading && loginMethod === 'google' ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <>
                          <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          <span>Continue with Google</span>
                          <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </>
                      )}
                    </Button>
                  </motion.div>

                  {/* Divider */}
                  <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[var(--isp-divider)]"></div></div>
                    <div className="relative flex justify-center"><span className="px-4 text-xs text-[var(--isp-muted)] bg-[var(--isp-glass)]">or use email</span></div>
                  </div>

                  {/* Email Option */}
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
                    <Button
                      onClick={() => setStep('email-input')}
                      variant="outline"
                      className="w-full h-12  border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)] hover:bg-[var(--isp-glass-inner-hover)] text-slate-900 font-medium flex items-center justify-center gap-2 group"
                      data-testid="email-option-button"
                    >
                      <Mail className="w-4 h-4 text-[var(--isp-muted)] group-hover:text-[var(--isp-accent)] transition-colors" />
                      <span>Continue with Email</span>
                    </Button>
                  </motion.div>

                  {/* Social proof (mobile) */}
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                    className="mt-6 pt-5 border-t border-[var(--isp-divider)] lg:hidden"
                  >
                    <div className="flex items-center justify-center gap-6 text-[var(--isp-muted)] text-xs">
                      <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /><span>10K+ students</span></div>
                      <div className="flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-[var(--isp-amber)]" /><span>4.9 rating</span></div>
                      <div className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-[var(--isp-accent)]" /><span>Secure</span></div>
                    </div>
                  </motion.div>
                </motion.div>
              )}

              {/* ── EMAIL INPUT STEP ── */}
              {step === 'email-input' && (
                <motion.div key="email-input" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                  <button onClick={() => setStep('welcome')} className="flex items-center gap-1 text-[var(--isp-muted)] hover:text-slate-900 text-sm mb-6 group">
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Back
                  </button>
                  <div className="text-center mb-8">
                    <div className="w-14 h-14 mx-auto mb-4  bg-[var(--isp-accent)]/10 border border-[var(--isp-accent)]/20 flex items-center justify-center">
                      <Mail className="w-7 h-7 text-[var(--isp-accent)]" />
                    </div>
                    <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">What's your email?</h2>
                    <p className="text-[var(--isp-body)] text-sm">We'll check if you have an account</p>
                  </div>
                  <form onSubmit={handleEmailNext}>
                    <div className="relative mb-4">
                      <Input
                        ref={emailInputRef} type="email" placeholder="your@email.com" value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`h-14 pl-4 pr-12 bg-[var(--isp-glass-inner)] border-2  text-slate-900 text-lg placeholder:text-[var(--isp-muted)] transition-all ${
                          emailValid === true ? 'border-[var(--isp-accent)]/50 focus:border-[var(--isp-accent)]' :
                          emailValid === false ? 'border-[var(--isp-coral)]/50 focus:border-[var(--isp-coral)]' :
                          'border-[var(--isp-glass-border)] focus:border-[var(--isp-accent)]/50'
                        }`}
                        data-testid="email-input"
                      />
                      {emailValid !== null && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          {emailValid ? <Check className="w-5 h-5 text-[var(--isp-accent)]" /> : <span className="text-[var(--isp-coral)] text-xs">Invalid</span>}
                        </div>
                      )}
                    </div>
                    <label className="flex items-center gap-2 mb-6 cursor-pointer group">
                      <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 rounded border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)] text-[var(--isp-accent)] focus:ring-[var(--isp-accent)]/50"
                      />
                      <span className="text-sm text-[var(--isp-muted)] group-hover:text-[var(--isp-body)] transition-colors">Remember me</span>
                    </label>
                    <div className="space-y-3">
                      <Button type="submit" disabled={!emailValid || loading}
                        className="w-full h-12  isp-btn-primary text-base flex items-center justify-center gap-2"
                        data-testid="email-next-button"
                      >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Continue <ArrowRight className="w-4 h-4" /></>)}
                      </Button>
                      <Button type="button" onClick={handleMagicLink} disabled={!emailValid || loading} variant="outline"
                        className="w-full h-12  border-[var(--isp-amber)]/30 bg-[var(--isp-amber)]/5 hover:bg-[var(--isp-amber)]/10 text-[var(--isp-amber)] font-medium flex items-center justify-center gap-2"
                        data-testid="magic-link-button"
                      >
                        <Wand2 className="w-4 h-4" /> Send Magic Link Instead
                      </Button>
                    </div>
                  </form>
                </motion.div>
              )}

              {/* ── PASSWORD INPUT STEP ── */}
              {step === 'password-input' && (
                <motion.div key="password-input" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                  <button onClick={() => setStep('email-input')} className="flex items-center gap-1 text-[var(--isp-muted)] hover:text-slate-900 text-sm mb-6 group">
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Back
                  </button>
                  <div className="text-center mb-8">
                    <div className="w-14 h-14 mx-auto mb-4  bg-[var(--isp-coral)]/10 border border-[var(--isp-coral)]/20 flex items-center justify-center">
                      {isNewUser ? <User className="w-7 h-7 text-[var(--isp-coral)]" /> : <Lock className="w-7 h-7 text-[var(--isp-accent)]" />}
                    </div>
                    {isNewUser ? (
                      <><h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Create your account</h2><p className="text-[var(--isp-body)] text-sm">Almost there! Just a few more details</p></>
                    ) : (
                      <><h2 className="font-heading text-xl font-bold text-slate-900 mb-2">{returningUser?.name ? `Hey ${returningUser.name.split(' ')[0]}!` : 'Welcome back!'}</h2><p className="text-[var(--isp-body)] text-sm">Enter your password to continue</p></>
                    )}
                    <p className="text-[var(--isp-accent)] text-sm mt-2">{email}</p>
                  </div>
                  <form onSubmit={handlePasswordSubmit} className="space-y-4">
                    {isNewUser && (
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--isp-muted)]" />
                        <Input type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required
                          className="h-14 pl-12 bg-[var(--isp-glass-inner)] border-2 border-[var(--isp-glass-border)]  text-slate-900 placeholder:text-[var(--isp-muted)] focus:border-[var(--isp-accent)]/50"
                          data-testid="name-input"
                        />
                      </div>
                    )}
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--isp-muted)]" />
                      <Input ref={passwordInputRef} type={showPassword ? 'text' : 'password'} placeholder="Password" value={password}
                        onChange={(e) => setPassword(e.target.value)} required
                        className="h-14 pl-12 pr-12 bg-[var(--isp-glass-inner)] border-2 border-[var(--isp-glass-border)]  text-slate-900 placeholder:text-[var(--isp-muted)] focus:border-[var(--isp-accent)]/50"
                        data-testid="password-input"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--isp-muted)] hover:text-[var(--isp-body)] transition-colors">
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {isNewUser && password && (
                      <div className="space-y-2">
                        <div className="flex gap-1">
                          {[25, 50, 75, 100].map((t) => {
                            const getBarColor = () => {
                              if (passwordStrength < t) return 'bg-white/10';
                              if (t <= 25) return 'bg-[var(--isp-coral)]';
                              if (t <= 50) return 'bg-[var(--isp-amber)]';
                              if (t <= 75) return 'bg-[var(--isp-accent)]/70';
                              return 'bg-[var(--isp-accent)]';
                            };
                            return (
                              <div key={t} className={`h-1 flex-1 rounded-full transition-colors ${getBarColor()}`} />
                            );
                          })}
                        </div>
                        <p className="text-xs text-[var(--isp-muted)]">
                          {passwordStrength <= 25 && 'Weak password'}{passwordStrength > 25 && passwordStrength <= 50 && 'Fair password'}
                          {passwordStrength > 50 && passwordStrength <= 75 && 'Good password'}{passwordStrength > 75 && 'Strong password'}
                        </p>
                      </div>
                    )}
                    <Button type="submit" disabled={loading || !password || (isNewUser && !name)}
                      className="w-full h-14  isp-btn-primary text-lg flex items-center justify-center gap-2"
                      data-testid="password-submit-button"
                    >
                      {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /><span>Almost there...</span></>) : (<><span>{isNewUser ? 'Create Account' : 'Sign In'}</span><Zap className="w-5 h-5" /></>)}
                    </Button>
                    {!isNewUser && (
                      <button type="button" onClick={handleMagicLink} className="w-full text-center text-sm text-[var(--isp-muted)] hover:text-[var(--isp-accent)] transition-colors py-2">
                        Forgot password? Use Magic Link
                      </button>
                    )}
                  </form>
                </motion.div>
              )}

              {/* ── MAGIC LINK SENT ── */}
              {step === 'magic-sent' && (
                <motion.div key="magic-sent" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3 }} className="text-center py-8">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200 }}>
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[var(--isp-amber)]/10 border border-[var(--isp-amber)]/30 flex items-center justify-center">
                      <Wand2 className="w-10 h-10 text-[var(--isp-amber)]" />
                    </div>
                  </motion.div>
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Check your inbox!</h2>
                  <p className="text-[var(--isp-body)] mb-6">We sent a magic link to<br /><span className="text-[var(--isp-accent)] font-medium">{email}</span></p>
                  <div className="bg-[var(--isp-glass-inner)]  p-4 mb-6 border border-[var(--isp-glass-border)]">
                    <p className="text-[var(--isp-muted)] text-sm">Click the link in the email to sign in instantly.<br />No password needed!</p>
                  </div>
                  <button onClick={() => setStep('email-input')} className="text-sm text-[var(--isp-muted)] hover:text-[var(--isp-body)] transition-colors">
                    Use a different email
                  </button>
                </motion.div>
              )}

              {/* ── 2FA VERIFY ── */}
              {step === '2fa-verify' && (
                <motion.div key="2fa-verify" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3 }} className="text-center py-4">
                  <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[var(--isp-blue)]/10 border border-[var(--isp-blue)]/30 flex items-center justify-center">
                    <Shield className="w-7 h-7 text-[var(--isp-blue)]" />
                  </div>
                  <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Two-Factor Verification</h2>
                  <p className="text-[var(--isp-body)] text-sm mb-6">A 6-digit code was sent to <span className="text-[var(--isp-accent)] font-medium">{twoFaEmailHint}</span></p>
                  <form onSubmit={handle2faVerify} className="space-y-4">
                    <Input value={twoFaCode} onChange={e => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000" maxLength={6}
                      className="text-center text-2xl tracking-[0.5em] font-bold  border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)] text-slate-900 h-14"
                      autoFocus data-testid="login-2fa-code"
                    />
                    <Button type="submit" disabled={loading || twoFaCode.length !== 6}
                      className="w-full h-12  isp-btn-primary font-bold text-base" data-testid="login-2fa-submit"
                    >{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Sign In'}</Button>
                  </form>
                  <button onClick={() => { setStep('password-input'); setTwoFaCode(''); }} className="mt-4 text-sm text-[var(--isp-muted)] hover:text-[var(--isp-body)] transition-colors">
                    <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to login
                  </button>
                </motion.div>
              )}

              {/* ── SUCCESS ── */}
              {step === 'success' && (
                <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} className="text-center py-8">
                  <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.2, type: "spring", stiffness: 200 }}>
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[var(--isp-accent)]/10 border-2 border-[var(--isp-accent)]/30 flex items-center justify-center isp-glow-green">
                      <Sparkles className="w-12 h-12 text-[var(--isp-accent)]" />
                    </div>
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                    <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">You're in!</h2>
                    <p className="text-[var(--isp-body)]">{isNewUser ? 'Welcome to iStudyPro!' : 'Welcome back!'}<br />Redirecting you now...</p>
                  </motion.div>
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-6">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--isp-accent)]" />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Trust strip below the card — keeps it visible without
              eating card real estate. */}
          <div className="mt-5 flex items-center justify-center gap-5 text-[11px] text-slate-500">
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" aria-hidden /> 10K+ students
            </div>
            <div className="flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5 text-amber-500" aria-hidden /> 4.9 rating
            </div>
            <div className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-[#002FA7]" aria-hidden /> Privacy first
            </div>
          </div>
        </motion.div>
    </div>
  );
};

export default LoginPage;
